import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  IconButton,
  Grid,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
} from '@mui/material';
import {
  Refresh,
  Add,
  Visibility,
  Edit,
  People,
  LocalShipping,
  TrendingUp,
  Settings,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { getPools, getPoolDetails, createPool, updatePoolStatus, updatePoolCosts, getPoolStats, processPool, getPoolPackages } from '../../services/groupShippingService';
import { fetchLogistics } from '../../services/logistics';
import CustomPagination from '../../components/common/CustomPagination';

const GroupShippingManagement = () => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [poolDetailDialog, setPoolDetailDialog] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const [createDialog, setCreateDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [costDialog, setCostDialog] = useState(false);
  const [processDialog, setProcessDialog] = useState(false);
  const [poolPackagesDialog, setPoolPackagesDialog] = useState(false);
  
  // Form states
  const [newPool, setNewPool] = useState({ shipping_method: 'sea', destination_country: 'Nigeria' });
  const [statusUpdate, setStatusUpdate] = useState({ status: '', tracking_number: '', notes: '' });
  const [costUpdate, setCostUpdate] = useState({ total_shipping_cost: '' });
  const [processForm, setProcessForm] = useState({ 
    logistics_id: '', 
    actual_shipping_method: '', 
    payment_deadline: '' 
  });
  const [availableLogistics, setAvailableLogistics] = useState([]);
  const [processingLoading, setProcessingLoading] = useState(false);
  const [poolPackages, setPoolPackages] = useState(null);

  useEffect(() => {
    loadPools();
    loadStats();
  }, [page, rowsPerPage, statusFilter, methodFilter]);

  const loadPools = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
      };
      
      if (statusFilter) params.status = statusFilter;
      if (methodFilter) params.shipping_method = methodFilter;
      if (searchQuery) params.search = searchQuery;
      
      const response = await getPools(params);
      setPools(response.results || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      showSnackbar(error.message || 'Failed to load pools', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await getPoolStats(30);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleViewPool = async (poolSid) => {
    try {
      const pool = await getPoolDetails(poolSid);
      setSelectedPool(pool);
      setPoolDetailDialog(true);
    } catch (error) {
      showSnackbar(error.message || 'Failed to load pool details', 'error');
    }
  };

  const handleCreatePool = async () => {
    try {
      await createPool(newPool);
      showSnackbar('Pool created successfully', 'success');
      setCreateDialog(false);
      setNewPool({ shipping_method: 'sea', destination_country: 'Nigeria' });
      loadPools();
      loadStats();
    } catch (error) {
      showSnackbar(error.message || 'Failed to create pool', 'error');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await updatePoolStatus(
        selectedPool.sid,
        statusUpdate.status,
        statusUpdate.tracking_number,
        statusUpdate.notes
      );
      showSnackbar('Pool status updated successfully', 'success');
      setStatusDialog(false);
      setStatusUpdate({ status: '', tracking_number: '', notes: '' });
      loadPools();
      if (poolDetailDialog) {
        handleViewPool(selectedPool.sid);
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to update pool status', 'error');
    }
  };

  const handleUpdateCosts = async () => {
    try {
      await updatePoolCosts(selectedPool.sid, parseFloat(costUpdate.total_shipping_cost));
      showSnackbar('Pool costs updated successfully', 'success');
      setCostDialog(false);
      setCostUpdate({ total_shipping_cost: '' });
      loadPools();
      if (poolDetailDialog) {
        handleViewPool(selectedPool.sid);
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to update pool costs', 'error');
    }
  };

  const handleProcessPool = async (pool) => {
    try {
      setSelectedPool(pool);
      // Load logistics for the pool's shipping method
      await loadLogisticsForMethod(pool.actual_shipping_method || pool.shipping_method);
      // Set default actual shipping method
      setProcessForm({
        logistics_id: '',
        actual_shipping_method: pool.actual_shipping_method || pool.shipping_method,
        payment_deadline: ''
      });
      setProcessDialog(true);
    } catch (error) {
      showSnackbar(error.message || 'Failed to load logistics', 'error');
    }
  };

  const loadLogisticsForMethod = async (method) => {
    try {
      const response = await fetchLogistics({ method, page_size: 100 });
      setAvailableLogistics(response.results || []);
    } catch (error) {
      console.error('Error loading logistics:', error);
      setAvailableLogistics([]);
    }
  };

  const handleProcessSubmit = async () => {
    if (!processForm.logistics_id || !processForm.payment_deadline) {
      showSnackbar('Please fill in all required fields', 'warning');
      return;
    }

    try {
      setProcessingLoading(true);
      const result = await processPool(selectedPool.sid, {
        logistics_id: processForm.logistics_id,
        actual_shipping_method: processForm.actual_shipping_method || selectedPool.shipping_method,
        payment_deadline: processForm.payment_deadline
      });
      
      showSnackbar(
        `Pool processed successfully!`,
        'success'
      );
      setProcessDialog(false);
      setProcessForm({ logistics_id: '', actual_shipping_method: '', payment_deadline: '' });
      loadPools();
      if (poolDetailDialog) {
        handleViewPool(selectedPool.sid);
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to process pool', 'error');
    } finally {
      setProcessingLoading(false);
    }
  };

  const handleViewPoolPackages = async (poolSid) => {
    try {
      const packages = await getPoolPackages(poolSid);
      setPoolPackages(packages);
      setPoolPackagesDialog(true);
    } catch (error) {
      showSnackbar(error.message || 'Failed to load pool packages', 'error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'success';
      case 'filling':
        return 'warning';
      case 'open':
        return 'info';
      case 'shipping':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      field: 'pool_id',
      headerName: 'Pool ID',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'shipping_method',
      headerName: 'Method',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value === 'air' ? 'Air' : 'Sea'}
          size="small"
          color={params.value === 'air' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'destination_country',
      headerName: 'Destination',
      width: 120,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'current_participants',
      headerName: 'Participants',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <People fontSize="small" />
          <Typography variant="body2">
            {params.value} / {params.row.max_participants}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'progress',
      headerName: 'Progress',
      width: 150,
      renderCell: (params) => {
        const progress = params.row.progress_percentage || 0;
        return (
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" sx={{ mt: 0.5 }}>
              {progress}%
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleViewPool(params.row.sid)}
            color="primary"
            title="View Details"
          >
            <Visibility fontSize="small" />
          </IconButton>
          {params.row.status === 'ready' && (
            <IconButton
              size="small"
              onClick={() => handleProcessPool(params.row)}
              color="success"
              title="Process Pool"
            >
              <LocalShipping fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => handleViewPoolPackages(params.row.sid)}
            color="info"
            title="View Packages"
          >
            <People fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
          Group Shipping Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadPools}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateDialog(true)}
            sx={{ backgroundColor: theme.palette.primary.main }}
          >
            Create Pool
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Pools
                </Typography>
                <Typography variant="h4">{stats.total_pools}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Participants
                </Typography>
                <Typography variant="h4">{stats.total_participants}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Completed Pools
                </Typography>
                <Typography variant="h4">{stats.completed_pools}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Savings (RMB)
                </Typography>
                <Typography variant="h4">¥{stats.total_savings.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="filling">Filling</MenuItem>
                  <MenuItem value="ready">Ready</MenuItem>
                  <MenuItem value="shipping">Shipping</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Shipping Method</InputLabel>
                <Select
                  value={methodFilter}
                  label="Shipping Method"
                  onChange={(e) => {
                    setMethodFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="sea">Sea</MenuItem>
                  <MenuItem value="air">Air</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth
                label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setPage(0);
                    loadPools();
                  }
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Pools Table */}
      <Card>
        <CardContent>
          <DataGrid
            rows={pools}
            columns={columns}
            getRowId={(row) => row.sid}
            loading={loading}
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            autoHeight
            hideFooter
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme.palette.primary.main,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: theme.palette.primary.contrastText,
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          />
          <CustomPagination
            page={page}
            totalPages={Math.ceil(totalCount / rowsPerPage) || 1}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Pool Detail Dialog */}
      <Dialog
        open={poolDetailDialog}
        onClose={() => setPoolDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Pool Details: {selectedPool?.pool_id}
        </DialogTitle>
        <DialogContent>
          {selectedPool && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Chip label={selectedPool.status} color={getStatusColor(selectedPool.status)} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Shipping Method</Typography>
                  <Typography>{selectedPool.shipping_method === 'air' ? 'Air' : 'Sea'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Destination</Typography>
                  <Typography>{selectedPool.destination_country}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">Participants</Typography>
                  <Typography>{selectedPool.current_participants} / {selectedPool.max_participants}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 12 }}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Participants
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Packages</TableCell>
                          <TableCell>Weight (KG)</TableCell>
                          <TableCell>Volume (CBM)</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPool.participants?.map((participant) => (
                          <TableRow key={participant.id}>
                            <TableCell>{participant.user_username || participant.user_email}</TableCell>
                            <TableCell>{participant.package_ids?.length || 0}</TableCell>
                            <TableCell>{participant.weight_kg}</TableCell>
                            <TableCell>{participant.volume_cbm}</TableCell>
                            <TableCell>
                              <Chip label={participant.status} size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setStatusUpdate({ status: selectedPool.status, tracking_number: selectedPool.tracking_number || '', notes: selectedPool.notes || '' });
                    setStatusDialog(true);
                  }}
                >
                  Update Status
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCostUpdate({ total_shipping_cost: selectedPool.total_shipping_cost || '' });
                    setCostDialog(true);
                  }}
                >
                  Update Costs
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPoolDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create Pool Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)}>
        <DialogTitle>Create New Pool</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
            <FormControl fullWidth>
              <InputLabel>Shipping Method</InputLabel>
              <Select
                value={newPool.shipping_method}
                label="Shipping Method"
                onChange={(e) => setNewPool({ ...newPool, shipping_method: e.target.value })}
              >
                <MenuItem value="sea">Sea</MenuItem>
                <MenuItem value="air">Air</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Destination Country"
              value={newPool.destination_country}
              onChange={(e) => setNewPool({ ...newPool, destination_country: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreatePool} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>Update Pool Status</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 300 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusUpdate.status}
                label="Status"
                onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="filling">Filling</MenuItem>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="shipping">Shipping</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Tracking Number"
              value={statusUpdate.tracking_number}
              onChange={(e) => setStatusUpdate({ ...statusUpdate, tracking_number: e.target.value })}
            />
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={statusUpdate.notes}
              onChange={(e) => setStatusUpdate({ ...statusUpdate, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateStatus} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Update Costs Dialog */}
      <Dialog open={costDialog} onClose={() => setCostDialog(false)}>
        <DialogTitle>Update Pool Costs</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, minWidth: 300 }}>
            <TextField
              fullWidth
              label="Total Shipping Cost (RMB)"
              type="number"
              value={costUpdate.total_shipping_cost}
              onChange={(e) => setCostUpdate({ total_shipping_cost: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCostDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateCosts} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Process Pool Dialog */}
      <Dialog open={processDialog} onClose={() => setProcessDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Process Pool: {selectedPool?.pool_id}</DialogTitle>
        <DialogContent>
          {selectedPool && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Alert severity="info">
                This will calculate shipping costs and distribute them to participants. Packages remain individual and are linked to the pool.
              </Alert>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>Pool Summary</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="textSecondary">Total Weight</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedPool.current_weight_kg} kg</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="textSecondary">Total Volume</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedPool.current_volume_cbm} CBM</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="textSecondary">Participants</Typography>
                    <Typography variant="body1" fontWeight="bold">{selectedPool.current_participants}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="textSecondary">Original Method</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPool.shipping_method === 'air' ? 'Air' : 'Sea'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              <FormControl fullWidth>
                <InputLabel>Actual Shipping Method (Can Override)</InputLabel>
                <Select
                  value={processForm.actual_shipping_method || selectedPool.shipping_method}
                  label="Actual Shipping Method (Can Override)"
                  onChange={(e) => {
                    setProcessForm({ ...processForm, actual_shipping_method: e.target.value });
                    loadLogisticsForMethod(e.target.value);
                  }}
                >
                  <MenuItem value="sea">Sea Freight</MenuItem>
                  <MenuItem value="air">Air Freight</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Logistics Provider *</InputLabel>
                {availableLogistics.length === 0 && processForm.actual_shipping_method ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Select
                    value={processForm.logistics_id}
                    label="Logistics Provider *"
                    onChange={(e) => setProcessForm({ ...processForm, logistics_id: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>Select Logistics Provider</em>
                    </MenuItem>
                    {availableLogistics.map((logistics) => (
                      <MenuItem key={logistics.id} value={logistics.id}>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {logistics.shipping_agent}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {logistics.terminal_port} • {logistics.goods_type}
                            {logistics.total_fee && ` • ₦${parseFloat(logistics.total_fee).toFixed(2)}/${logistics.method === 'air' ? 'kg' : 'CBM'}`}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>

              <TextField
                fullWidth
                label="Payment Deadline *"
                type="datetime-local"
                value={processForm.payment_deadline}
                onChange={(e) => setProcessForm({ ...processForm, payment_deadline: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Same deadline for all participants"
              />

              {processForm.logistics_id && selectedPool && (
                <Alert severity="success">
                  Estimated Cost: ₦
                  {(() => {
                    const logistics = availableLogistics.find(l => l.id === parseInt(processForm.logistics_id));
                    if (!logistics) return '0.00';
                    const method = processForm.actual_shipping_method || selectedPool.shipping_method;
                    const cost = method === 'air'
                      ? parseFloat(logistics.total_fee) * parseFloat(selectedPool.current_weight_kg)
                      : parseFloat(logistics.total_fee) * parseFloat(selectedPool.current_volume_cbm);
                    return cost.toFixed(2);
                  })()}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialog(false)} disabled={processingLoading}>Cancel</Button>
          <Button 
            onClick={handleProcessSubmit} 
            variant="contained" 
            color="success"
            disabled={processingLoading || !processForm.logistics_id || !processForm.payment_deadline}
            startIcon={processingLoading ? <CircularProgress size={20} /> : <LocalShipping />}
          >
            {processingLoading ? 'Processing...' : 'Process Pool'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pool Packages Dialog */}
      <Dialog open={poolPackagesDialog} onClose={() => setPoolPackagesDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Packages in Pool: {poolPackages?.pool_id}
        </DialogTitle>
        <DialogContent>
          {poolPackages && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Packages in Pool ({poolPackages.total_packages})
              </Typography>
              
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Participant</TableCell>
                      <TableCell>Package Number</TableCell>
                      <TableCell>Weight (kg)</TableCell>
                      <TableCell>Volume (CBM)</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Payment Status</TableCell>
                      <TableCell>Cost Share (RMB)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {poolPackages.participants?.map((participant) => 
                      participant.packages?.map((pkg, idx) => (
                        <TableRow key={`${participant.participant_id}-${pkg.id}-${idx}`}>
                          <TableCell>
                            {participant.user.username} ({participant.user.email})
                          </TableCell>
                          <TableCell>{pkg.package_number}</TableCell>
                          <TableCell>{pkg.package_weight}</TableCell>
                          <TableCell>{pkg.package_cbm}</TableCell>
                          <TableCell>
                            <Chip label={pkg.packaging_status} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={participant.payment_status} 
                              size="small"
                              color={participant.payment_status === 'paid' ? 'success' : 'warning'}
                            />
                          </TableCell>
                          <TableCell>¥{participant.pooled_shipping_cost || '0.00'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPoolPackagesDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupShippingManagement;
